
import { supabase } from "@/integrations/supabase/client";
import { Flight } from "@/types/flight";

interface UnifiedFlightRow {
  id: string;
  flight_number: string;
  dep_datetime: string;
  arr_datetime: string | null;
  status: string;
  status_reason: string | null;
  gate: string | null;
  etd: string | null;
  eta: string | null;
  organization_id: string;
  departure_city_id: string;
  arrival_city_id: string;
  aircraft_type_id: string | null;
  source: string;
}

/**
 * Reshapes a flat unified_flights row into the nested Flight type
 * expected by all UI components. Lookups for names/codes are done
 * separately after fetching.
 */
function reshapeToFlight(
  row: UnifiedFlightRow,
  orgMap: Map<string, { id: string; name: string; code: string }>,
  cityMap: Map<string, { id: string; name: string; code: string }>,
  aircraftMap: Map<string, { id: string; name: string; code: string }>,
): Flight {
  const org = orgMap.get(row.organization_id);
  const depCity = cityMap.get(row.departure_city_id);
  const arrCity = cityMap.get(row.arrival_city_id);
  const aircraft = row.aircraft_type_id ? aircraftMap.get(row.aircraft_type_id) : undefined;

  return {
    id: row.id,
    flight_number: row.flight_number,
    scheduled_departure: row.dep_datetime,
    scheduled_arrival: row.arr_datetime ?? row.dep_datetime,
    status: row.status as Flight['status'],
    status_reason: row.status_reason ?? undefined,
    gate: row.gate ?? undefined,
    etd: row.etd ?? undefined,
    eta: row.eta ?? undefined,
    source: row.source,
    created_at: '',
    updated_at: '',
    created_by: '',
    updated_by: '',
    organization_id: row.organization_id,
    departure_city_id: row.departure_city_id,
    arrival_city_id: row.arrival_city_id,
    aircraft_type_id: row.aircraft_type_id ?? '',
    organization: org ? { id: org.id, name: org.name, code: org.code } : undefined,
    departure_city: depCity ? { id: depCity.id, name: depCity.name, code: depCity.code } : undefined,
    arrival_city: arrCity ? { id: arrCity.id, name: arrCity.name, code: arrCity.code } : undefined,
    aircraft_type: aircraft ? { id: aircraft.id, name: aircraft.name, code: aircraft.code } : undefined,
  };
}

/**
 * Fetches flights from the unified_flights view which merges
 * the flights table (live data) with flight_daily_cache (scheduled patterns).
 * Performs secondary lookups for organization, city, and aircraft names.
 */
export async function fetchUnifiedFlights(options?: {
  gteDate?: string;
  ltDate?: string;
}): Promise<Flight[]> {
  let query = supabase
    .from('unified_flights' as any)
    .select('*');

  if (options?.gteDate) {
    query = query.gte('dep_datetime', options.gteDate);
  }
  if (options?.ltDate) {
    query = query.lt('dep_datetime', options.ltDate);
  }

  query = query.order('dep_datetime', { ascending: true });

  // Fetch flights and lookup data in parallel
  const [flightsResult, orgsResult, citiesResult, aircraftResult] = await Promise.all([
    query,
    supabase.from('organizations').select('id, name, code'),
    supabase.from('cities').select('id, name, code'),
    supabase.from('aircraft_types').select('id, name, code'),
  ]);

  if (flightsResult.error) {
    console.error('Error fetching unified flights:', flightsResult.error);
    throw flightsResult.error;
  }

  // Build lookup maps
  const orgMap = new Map<string, { id: string; name: string; code: string }>();
  (orgsResult.data ?? []).forEach((o: any) => {
    orgMap.set(o.id, o);
    orgMap.set(o.code, o); // Also map by code for cache rows
  });

  const cityMap = new Map<string, { id: string; name: string; code: string }>();
  (citiesResult.data ?? []).forEach((c: any) => cityMap.set(c.id, c));

  const aircraftMap = new Map<string, { id: string; name: string; code: string }>();
  (aircraftResult.data ?? []).forEach((a: any) => aircraftMap.set(a.id, a));

  const rows = (flightsResult.data ?? []) as unknown as UnifiedFlightRow[];
  return rows.map(row => reshapeToFlight(row, orgMap, cityMap, aircraftMap));
}
